/* eslint-disable jsx-a11y/mouse-events-have-key-events */

import {SankeyExtraProperties, SankeyLink, sankeyLinkHorizontal, SankeyNode} from 'd3-sankey';
import React, {Dispatch, SetStateAction} from 'react';

import {isHtmlElement} from '../../../../../../libs/shared/helpers/src/browser/domHelpers';
import {Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {SankeyId, SLinkExtra, SNodeExtra} from './sankey';

/*
 * Constants.
 */

const DEFAULT_OPACITY = 0.2;
const HOVER_OPACITY = 0.3;
const LOADING_OPACITY = 0.6;

/*
 * Props.
 */

interface SankeyPathProps<N extends SankeyExtraProperties, L extends SankeyExtraProperties> {
  link: SankeyLink<N, L>;
  i: number;
  isLoading?: boolean;
  setHighlightLinkIndexes: Dispatch<SetStateAction<Array<number>>>;
  highlightLinkIndexes: Array<number>;
  onHover?: (data: SankeyLink<SNodeExtra, SLinkExtra>, bounds?: DOMRect) => void;
  onUnhover?: Handler;
  onClick?: HandlerOf<SankeyLink<SNodeExtra, SLinkExtra>>;
  selectedLinkId?: SankeyId;
}

/**
 * Component.
 * Renders the curved path between 2 nodes.
 */

const path = sankeyLinkHorizontal();

export const SankeyPath = <N extends SNodeExtra, L extends SLinkExtra>({
  link,
  i,
  isLoading,
  setHighlightLinkIndexes,
  highlightLinkIndexes,
  onHover,
  onUnhover,
  onClick,
  selectedLinkId,
}: SankeyPathProps<N, L>) => {
  const handlePathMouseOver = (event: React.MouseEvent<SVGPathElement, MouseEvent>) => {
    if (isLoading) {
      return;
    }

    if (!isHtmlElement(event.target)) {
      return;
    }

    setHighlightLinkIndexes([...highlightLinkIndexes, i]);
    if (onHover) {
      onHover(link, event.target.getBoundingClientRect());
    }
  };
  const handlePathMouseOut = () => {
    if (isLoading) {
      return;
    }

    setHighlightLinkIndexes(highlightLinkIndexes.filter((index) => index !== i));
    if (onUnhover) {
      onUnhover();
    }
  };

  return (
    <path
      // Bit of a hack but because of https://github.com/d3/d3-sankey/issues/28#issuecomment-306517298 we need to add a tiny amount of ink
      // on each curve to prevent the gradients from not being displayed on horizontal paths.
      // This is not visible to the eye.
      d={(path(link) && `${path(link)} m0,0.001`) || undefined}
      stroke={
        isLoading
          ? 'url(#grad-loading)'
          : `url(#grad-${(link.source as SankeyNode<N, L>).index}-${(link.target as SankeyNode<N, L>).index})`
      }
      strokeWidth={Math.max(1, link.width!)}
      opacity={getPathOpacity(highlightLinkIndexes, i, link.id, selectedLinkId, isLoading)}
      fill="none"
      onMouseOver={handlePathMouseOver}
      onMouseOut={handlePathMouseOut}
      onFocus={() => !isLoading && onHover && onHover(link)}
      onClick={() => !isLoading && onClick && onClick(link)}
    />
  );
};

/*
 * Helpers.
 */

function getPathOpacity(
  highlightLinkIndexes: Array<number>,
  i: number,
  linkId?: SankeyId,
  selectedLinkId?: SankeyId,
  isLoading?: boolean,
) {
  if (isLoading) {
    return LOADING_OPACITY;
  }

  if (highlightLinkIndexes.includes(i) || (selectedLinkId && selectedLinkId === linkId)) {
    return HOVER_OPACITY;
  }

  return DEFAULT_OPACITY;
}
